import { create } from "zustand";
import { api, ApiError } from "../api/http.js";
import { useBalances, type Balances } from "./balances.js";
import { setLocale } from "../i18n/index.js";
import { configureWsRecovery, connectWs, disconnectWs, suspendWs } from "../api/ws.js";
import { advanceRequestScope, assertRequestScope, identifyRequestScope, onRequestScopeReset, requestScope, resetRequestScope, SupersededRequest } from "../api/scope.js";
import { hydrateDomains, invalidateDomainsAfterRecovery, resetDomains } from "./bootstrap.js";
import type { BaseState } from "../base/api.js";
import type { ProgressionState } from "../base/progression-api.js";
import type { CosmeticView } from "../store/types.js";
import type { DailyView, EventModifier, EventRunView, GrowthView } from "../events/types.js";
import type { Cursor } from "../api/realtime.js";

export interface Account {
  id: string;
  name: string;
  email: string | null;
  role: "player" | "admin" | "bot";
  locale: "de" | "en";
  createdAt: number;
  lastSeenAt: number;
  mustChangePassword: boolean;
}

export interface VipStatus {
  level: number;
  megaRank: number;
  points: number;
  nextThreshold: number;
  currentThreshold: number;
  progressPct: number;
  unlocks: string[];
  productionPct: number;
  buildSpeedPct: number;
  unitStatPct: number;
}

export interface Settings {
  locale: "de" | "en";
  notifications: Record<string, boolean>;
  reducedMotion: boolean;
  graphicsQuality: "auto" | "low" | "medium" | "high";
  sound: number;
  music: number;
  backgroundMusic: boolean;
  announcements: boolean;
  /** Auto-equip on grant (D69). */
  autoEquipUnits: boolean;
  autoEquipModules: boolean;
  /** From VIP 4 the normal start button covers missing costs from depot crates (D75). */
  autoCoverCosts: boolean;
}

export interface MeState {
  account: Account;
  balances: Balances;
  inventory: { itemId: string; qty: number }[];
  vip: VipStatus;
  settings: Settings;
  dayKey: string;
  nextReset: number;
  content: { version: number };
  base: BaseState;
  progression: ProgressionState;
  cosmetics: CosmeticView[];
  events: { runs: EventRunView[]; modifiers: EventModifier[] };
  daily: DailyView;
  growth: GrowthView;
  navigation: { chat: { global: number; system: number; dm: number }; mail: number; store: { flash: number; offers: number } };
  realtime: Cursor;
}

interface SessionState {
  status: "loading" | "anon" | "authed";
  account: Account | null;
  vip: VipStatus | null;
  settings: Settings | null;
  inventory: { itemId: string; qty: number }[];
  dayKey: string | null;
  nextReset: number | null;
  error: string | null;
  recoveryVersion: number;
  load(recover?: boolean): Promise<void>;
  login(name: string, password: string): Promise<void>;
  register(name: string, password: string, invite?: string): Promise<void>;
  logout(all?: boolean): Promise<void>;
  applyState(state: MeState): void;
  updateSettings(patch: Partial<Settings>): Promise<void>;
}

let loading: { generation: number; promise: Promise<void>; recover: boolean; token: object } | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;
let authQueue: Promise<unknown> = Promise.resolve();
function serializeAuth<T>(action: () => Promise<T>): Promise<T> {
  const operation = authQueue.then(action, action);
  authQueue = operation.catch(() => undefined);
  return operation;
}
onRequestScopeReset(() => { loading = null; if (retry) clearTimeout(retry); retry = null; resetDomains(); });

export const useSession = create<SessionState>((set, get) => ({
  status: "loading",
  account: null,
  vip: null,
  settings: null,
  inventory: [],
  dayKey: null,
  nextReset: null,
  error: null,
  recoveryVersion: 0,
  applyState: (state) => {
    if (get().account && get().account!.id !== state.account.id) resetRequestScope();
    identifyRequestScope(state.account.id);
    hydrateDomains(state);
    useBalances.getState().setBalances(state.balances);
    setLocale(state.settings.locale);
    set({ status: "authed", account: state.account, vip: state.vip, settings: state.settings, inventory: state.inventory, dayKey: state.dayKey, nextReset: state.nextReset, error: null });
  },
  load: (recover = false) => {
    if (recover && !(loading?.generation === requestScope().generation && loading.recover)) advanceRequestScope();
    const { generation } = requestScope();
    if (loading?.generation === generation) return loading.promise;
    if (retry) clearTimeout(retry); retry = null;
    suspendWs();
    const token = {};
    const promise = (async () => {
      try {
        const state = await api<MeState>("GET", "/api/me/state");
        assertRequestScope(generation);
        const recovering = recover || loading?.recover === true;
        if (recovering) invalidateDomainsAfterRecovery();
        get().applyState(state);
        if (recovering) set((s) => ({ recoveryVersion: s.recoveryVersion + 1 }));
        connectWs(state.account.id, state.realtime);
      } catch (error) {
        if (error instanceof SupersededRequest || generation !== requestScope().generation) return;
        if (error instanceof ApiError && error.status === 401) {
          resetRequestScope(); set({ status: "anon", account: null, vip: null, settings: null, inventory: [], dayKey: null, nextReset: null, error: null });
          return;
        }
        set({ error: error instanceof Error ? error.message : String(error) });
        if (recover || loading?.recover === true) throw error;
        retry = setTimeout(() => { retry = null; if (generation === requestScope().generation) void get().load(); }, 2000);
      } finally { if (loading?.token === token) loading = null; }
    })();
    loading = { generation, promise, recover, token };
    return promise;
  },
  login: async (name, password) => {
    resetRequestScope(); set({ status: "anon", account: null, vip: null, settings: null, inventory: [] });
    const { generation } = requestScope();
    await serializeAuth(async () => { assertRequestScope(generation); await api("POST", "/api/auth/login", { name, password }); });
    assertRequestScope(generation);
    await get().load();
  },
  register: async (name, password, invite) => {
    await api("POST", "/api/auth/register", { name, password, ...(invite ? { invite } : {}) });
    await get().login(name, password);
  },
  logout: async (all = false) => {
    resetRequestScope();
    const { generation } = requestScope();
    const signingOut = serializeAuth(() => api("POST", all ? "/api/auth/logout-all" : "/api/auth/logout"));
    set({ status: "anon", account: null, vip: null, settings: null, inventory: [], dayKey: null, nextReset: null, error: null });
    try {
      await signingOut;
    } catch (error) {
      if (!(error instanceof SupersededRequest)) throw error;
    } finally {
      if (generation === requestScope().generation) disconnectWs();
    }
  },
  updateSettings: async (patch) => {
    const { settings } = await api<{ settings: Settings }>("PATCH", "/api/me/settings", patch);
    setLocale(settings.locale);
    set({ settings });
  },
}));

configureWsRecovery(() => useSession.getState().load(true));
const unsubscribeScope = useSession.subscribe((next, previous) => {
  if (next.account?.id === previous.account?.id || (next.account?.id ?? null) === requestScope().accountId) return;
  resetRequestScope();
  if (next.account) identifyRequestScope(next.account.id);
});
if (import.meta.hot) import.meta.hot.dispose(unsubscribeScope);
